import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { JointBillTableRowLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { Badge } from "@components/Badge/Badge";
import { DotIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import {
  type IOverviewJointBillService,
  type IRequestedSpBillData,
} from "@services/overview/types";
import {
  useGetRequestedSpBillFileMutation,
  useRemoveRequestedSpMutation,
} from "@services/serviceProviders/mutations";
import { isNullOrUndefined, openPdfPreview } from "@utils/common";
import { ProviderBillNameCell } from "../../../ProviderBill/components/ProviderBillNameCell/ProviderBillNameCell";
import { ProviderBillTotalCell } from "../../../ProviderBill/components/ProviderBillTotalCell/ProviderBillTotalCell";
import { requestedBillStatusBadgeColorMap } from "../../types";
import { RequestedProviderBillNameCell } from "../RequestedProviderBillNameCell/RequestedProviderBillNameCell";
import { RequiredProviderBillOptions } from "../RequiredProviderBillOptions/RequiredProviderBillOptions";

interface IRequestedSpBillProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  requestedSpBillData: IRequestedSpBillData;
  fullWidthBorderTop?: boolean;
}

export const RequestedSpBill: React.FC<IRequestedSpBillProps> = ({
  jointBillServiceId,
  periodId,
  requestedSpBillData,
  fullWidthBorderTop,
}) => {
  const { t } = useTranslation();
  const {
    serviceProviderName,
    serviceProviderNameIsKnown,
    requestedServiceProviderBillStateId,
    requestedServiceProviderBillId,
    requestedServiceProviderBillFileExist,
  } = requestedSpBillData;

  const { isRequestedServiceProvidersCollapseEnabled } = useFeatureConfig();

  const { toastApiError } = useApiErrorUtils();
  const { mutateAsync: removeRequestedSp, isPending: isRemovePending } =
    useRemoveRequestedSpMutation(
      jointBillServiceId,
      requestedServiceProviderBillId,
    );

  const { mutateAsync: getBillFile, isPending: isFileDownloadPending } =
    useGetRequestedSpBillFileMutation();

  const remove = async () => {
    const result = await removeRequestedSp().catch(toastApiError);

    if (!isNullOrUndefined(result)) {
      toast.success(t("bill.requestedSp.interactions.remove.toast.success"));
    }
  };

  const openPdf = async () => {
    const response = await getBillFile(requestedServiceProviderBillId).catch(
      toastApiError,
    );

    if (!isNullOrUndefined(response)) {
      openPdfPreview(response.fileName, response.contentBase64);
    }
  };

  const statusBadge = (
    <Tooltip
      content={t(
        `bill.requestedSp.state.${requestedServiceProviderBillStateId}.description`,
      )}
    >
      {!isRequestedServiceProvidersCollapseEnabled ? (
        <Badge
          color={
            requestedBillStatusBadgeColorMap[
              requestedServiceProviderBillStateId
            ]
          }
          variant="bubble"
          icon={DotIcon}
          iconClassName="size-2"
          size="small"
        >
          {t(
            `bill.requestedSp.state.${requestedServiceProviderBillStateId}.title`,
          )}
        </Badge>
      ) : (
        <span className="text-sm text-grey-dark">
          {t(
            `bill.requestedSp.state.${requestedServiceProviderBillStateId}.title`,
          )}
        </span>
      )}
    </Tooltip>
  );

  if (!isRequestedServiceProvidersCollapseEnabled) {
    return (
      <JointBillTableRowLayout
        fullWidthBorderTop={fullWidthBorderTop}
        nameCell={
          <ProviderBillNameCell
            name={
              serviceProviderNameIsKnown
                ? serviceProviderName
                : t("bill.requestedSp.billName")
            }
            shouldDisplayRemoveButton={true}
            isRemoveProviderEnabled={true}
            isRemoveProviderLoading={isRemovePending}
            isBillFileAvailable={requestedServiceProviderBillFileExist}
            isBillFileLoading={isFileDownloadPending}
            onBillFileClick={openPdf}
            onRemoveProviderClick={remove}
            statusBadge={statusBadge}
            removeButtonTooltipContent={t(
              "bill.requestedSp.interactions.remove.tooltip",
            )}
          />
        }
        statusCell={statusBadge}
        totalCell={
          <ProviderBillTotalCell
            jointBillServiceId={jointBillServiceId}
            periodId={periodId}
            isProviderRemoveButtonEnabled={true}
            shouldHideCheckbox={true}
            onRemoveProviderClick={remove}
          />
        }
      />
    );
  }

  return (
    <div className="group/requested-bill flex items-center justify-between py-4 md:py-[18px] md:pr-3">
      <div className="block md:flex md:w-full md:justify-between md:gap-4">
        <RequestedProviderBillNameCell
          variant="serviceProvider"
          name={
            serviceProviderNameIsKnown
              ? serviceProviderName
              : t("bill.requestedSp.billName")
          }
          shouldDisplayRemoveButton={true}
          isRemoveProviderEnabled={true}
          isRemoveProviderLoading={isRemovePending}
          onRemoveProviderClick={remove}
          statusBadge={statusBadge}
          removeButtonTooltipContent={t(
            "bill.requestedSp.interactions.remove.tooltip",
          )}
          shouldDisplayDocumentButton={requestedServiceProviderBillFileExist}
          isDocumentButtonInteractionLoading={isFileDownloadPending}
          onDocumentButtonClick={openPdf}
          documentButtonTooltipContent={t(
            "bill.interactions.downloadBillFile.tooltip",
          )}
        />
        <div className="hidden md:block">{statusBadge}</div>
      </div>
      <RequiredProviderBillOptions
        isProviderRemoveButtonEnabled={true}
        onRemoveProviderClick={remove}
        isProviderHintEnabled
        providerHintText={t(
          `bill.requestedSp.state.${requestedServiceProviderBillStateId}.description`,
        )}
      />
    </div>
  );
};
